import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  SignalCellularAlt as ServicesIcon,
  Warning as OutageIcon,
  Schedule as PlannedIcon,
  Assignment as CaseIcon,
  ConfirmationNumber as TicketIcon,
  Checklist as TaskIcon,
  ShoppingBagOutlined as OrdersIcon,
  Refresh as RefreshIcon,
  ShowChart as MrrIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import apiService from '../services/api';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

type FieldOpt = { value: string; label: string; sort_order: number };

function buildStatusRows(
  options: FieldOpt[],
  counts: Record<string, number>
): { value: string; label: string; count: number }[] {
  const sortedOpts = [...options].sort((a, b) => a.sort_order - b.sort_order);
  const rows: { value: string; label: string; count: number }[] = [];
  const used = new Set<string>();
  for (const o of sortedOpts) {
    const n = counts[o.value] || 0;
    if (n > 0) {
      rows.push({ value: o.value, label: o.label, count: n });
      used.add(o.value);
    }
  }
  const extras = Object.entries(counts)
    .filter(([k, v]) => v > 0 && !used.has(k))
    .map(([k, v]) => ({ value: k, label: k, count: v }))
    .sort((a, b) => a.value.localeCompare(b.value));
  return [...rows, ...extras];
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%', minHeight: 100, display: 'flex', alignItems: 'center' }}>
    <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ color, opacity: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 2, bgcolor: `${color}14` }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {title}
        </Typography>
        <Typography variant="h4" component="span" sx={{ color, fontWeight: 700, letterSpacing: '-0.02em' }}>
          {value.toLocaleString()}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const crmCardSx = {
  height: '100%',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

interface NetworkStatus { online: number; offline: number; stale: number; unknown: number; total: number; live_status?: boolean }

const NetworkStatusTile: React.FC<{ title: string; status: NetworkStatus; reportType: string; reportLabel: string; pendingNote?: string }> = ({
  title, status, reportType, reportLabel, pendingNote,
}) => (
        <Card sx={crmCardSx}>
          <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
              <Box sx={{ color: '#0369a1', display: 'flex', p: 1, borderRadius: 2, bgcolor: 'rgba(3,105,161,0.08)' }}>
                <ServicesIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {title}
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
                <svg width="48" height="48" viewBox="0 0 48 48">
                  <circle cx="24" cy="24" r="18" fill="none" stroke="#e5e7eb" strokeWidth="7" />
                  {status.total > 0 && (
                    <circle
                      cx="24" cy="24" r="18"
                      fill="none"
                      stroke="#16a34a"
                      strokeWidth="7"
                      strokeDasharray={`${(status.online / status.total) * 2 * Math.PI * 18} ${2 * Math.PI * 18}`}
                      strokeDashoffset={2 * Math.PI * 18 * 0.25}
                      strokeLinecap="round"
                    />
                  )}
                </svg>
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1, color: '#16a34a' }}>
                    {status.online}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[
                { label: 'Online', count: status.online, color: '#16a34a' },
                { label: 'Stale', count: status.stale, color: '#d97706' },
                { label: 'Offline', count: status.offline, color: '#dc2626' },
                ...(status.unknown > 0 ? [{ label: 'Unknown', count: status.unknown, color: '#9ca3af' }] : []),
              ].map(({ label, count, color }) => (
                <Box
                  key={label}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'text.primary', minWidth: 28, textAlign: 'right' }}>
                    {count}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              <Typography component={RouterLink} to={`/reports/services?service_type=${reportType}`} variant="caption" color="primary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                {reportLabel}
              </Typography>
            </Typography>
            {pendingNote && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                {pendingNote}
              </Typography>
            )}
          </CardContent>
        </Card>
);

const DashboardPage: React.FC = () => {
  const [totalAccounts, setTotalAccounts] = useState<number>(0);
  const [totalServices, setTotalServices] = useState<number>(0);
  /** Count of active services per service type (for “Active Services By Type” widget). */
  const [activeServicesByType, setActiveServicesByType] = useState<Record<string, number>>({});
  const [serviceTypes, setServiceTypes] = useState<{ code: string; name: string; sort_order?: number }[]>([]);
  const [activeNbnOutages, setActiveNbnOutages] = useState<number>(0);
  const [plannedNbnOutages, setPlannedNbnOutages] = useState<number>(0);
  const [activeVoiceOutages, setActiveVoiceOutages] = useState<number>(0);
  const [plannedVoiceOutages, setPlannedVoiceOutages] = useState<number>(0);
  const [activeWebServicesOutages, setActiveWebServicesOutages] = useState<number>(0);
  const [plannedWebServicesOutages, setPlannedWebServicesOutages] = useState<number>(0);
  const [activeMobileOutages, setActiveMobileOutages] = useState<number>(0);
  const [plannedMobileOutages, setPlannedMobileOutages] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [connectionFailures, setConnectionFailures] = useState<any[]>([]);
  const [caseStatusCounts, setCaseStatusCounts] = useState<Record<string, number>>({});
  const [ticketStatusCounts, setTicketStatusCounts] = useState<Record<string, number>>({});
  const [taskStatusCounts, setTaskStatusCounts] = useState<Record<string, number>>({});
  const [taskStatusOptions, setTaskStatusOptions] = useState<FieldOpt[]>([]);
  const [caseStatusOptions, setCaseStatusOptions] = useState<FieldOpt[]>([]);
  const [ticketStatusOptions, setTicketStatusOptions] = useState<FieldOpt[]>([]);
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [nbnStatus, setNbnStatus] = useState<NetworkStatus | null>(null);
  const [supaStatus, setSupaStatus] = useState<{ online: number; offline: number; stale: number; unknown: number; total: number } | null>(null);
  const [orderMonthlyStats, setOrderMonthlyStats] = useState<{ month: string; order_count: number; total_mrr: number }[]>([]);
  const [googleReviewStats, setGoogleReviewStats] = useState<{ average_rating: number | null; total_review_count: number | null; place_id?: string } | null | undefined>(undefined);
  const [carrierBannerExpanded, setCarrierBannerExpanded] = useState(true);

  const fetchAll = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const accountStats = await apiService.getAccountStatistics();
      setTotalAccounts(accountStats.total_accounts || 0);

      // Service types are configurable in Settings → Service Types.
      // Use them to drive the “Active Services By Type” widget so new types
      // like Domain Name are surfaced automatically.
      const typesResp = await apiService.getServiceTypes(false).catch(() => []);
      const typeRows = (Array.isArray(typesResp) ? typesResp : [])
        .map((t: any) => ({
          code: String(t.code || '').trim().toLowerCase(),
          name: String(t.name || t.code || '').trim() || String(t.code || '').trim(),
          sort_order: typeof t.sort_order === 'number' ? t.sort_order : 0,
        }))
        .filter((t: any) => t.code);
      setServiceTypes(typeRows);

      const svcResp = await apiService.searchServices({ page: 1, per_page: 1, status: 'active' });
      setTotalServices(svcResp.total || 0);

      const types = typeRows.length
        ? typeRows.map((t: any) => t.code)
        : ['nbn', 'mobile', 'voice', 'sonweb', 'data', 'supa_networks'];
      const byTypeEntries = await Promise.all(
        types.map(async (t) => {
          const resp = await apiService.searchServices({
            page: 1,
            per_page: 1,
            service_type: t,
            status: 'active',
          });
          return [t, resp.total || 0] as [string, number];
        })
      );
      // Only show types that actually have active services to reduce noise.
      setActiveServicesByType(Object.fromEntries(byTypeEntries.filter(([, n]) => n > 0)));

      try {
        const [
          nbnActive, nbnPlanned, voiceActive, voicePlanned,
          webServicesActive, webServicesPlanned, mobileActive, mobilePlanned
        ] = await Promise.all([
          apiService.getOutages(new URLSearchParams([['status', 'active'], ['service_type', 'nbn'], ['limit', '1000']])).catch(() => []),
          apiService.getOutages(new URLSearchParams([['status', 'planned'], ['service_type', 'nbn'], ['limit', '1000']])).catch(() => []),
          apiService.getVoiceOutages({ status: 'active', limit: '1000' }).catch(() => ({ outages: [] })),
          apiService.getVoiceOutages({ status: 'planned', limit: '1000' }).catch(() => ({ outages: [] })),
          apiService.getWebServicesOutages({ status: 'active', limit: '1000' }).catch(() => ({ outages: [] })),
          apiService.getWebServicesOutages({ status: 'planned', limit: '1000' }).catch(() => ({ outages: [] })),
          apiService.getMobileOutages({ status: 'active', limit: '1000' }).catch(() => []),
          apiService.getMobileOutages({ status: 'planned', limit: '1000' }).catch(() => [])
        ]);

        setActiveNbnOutages(Array.isArray(nbnActive) ? nbnActive.length : ((nbnActive as any)?.outages?.length || 0));
        setPlannedNbnOutages(Array.isArray(nbnPlanned) ? nbnPlanned.length : ((nbnPlanned as any)?.outages?.length || 0));
        setActiveVoiceOutages((voiceActive as any)?.outages?.length || 0);
        setPlannedVoiceOutages((voicePlanned as any)?.outages?.length || 0);
        setActiveWebServicesOutages((webServicesActive as any)?.outages?.length || 0);
        setPlannedWebServicesOutages((webServicesPlanned as any)?.outages?.length || 0);
        setActiveMobileOutages(Array.isArray(mobileActive) ? mobileActive.length : 0);
        setPlannedMobileOutages(Array.isArray(mobilePlanned) ? mobilePlanned.length : 0);
      } catch (outageErr: any) {
        console.error('Error fetching outages:', outageErr);
        setActiveNbnOutages(0); setPlannedNbnOutages(0);
        setActiveVoiceOutages(0); setPlannedVoiceOutages(0);
        setActiveWebServicesOutages(0); setPlannedWebServicesOutages(0);
        setActiveMobileOutages(0); setPlannedMobileOutages(0);
      }

      try {
        const failuresData = await apiService.getConnectionFailures();
        setConnectionFailures(failuresData.connection_failures || []);
      } catch {
        setConnectionFailures([]);
      }

      try {
        const [caseCountResp, caseOpts, ticketCountResp, ticketOptsGrouped] = await Promise.all([
          apiService.getCaseStatusCounts(),
          apiService.getCaseFieldOptionsGrouped(),
          apiService.getSupportDeskTicketStatusCounts(),
          apiService.getSupportDeskFieldOptionsGrouped(),
        ]);
        setCaseStatusCounts(caseCountResp.counts || {});
        setTicketStatusCounts(ticketCountResp.counts || {});
        setCaseStatusOptions((caseOpts.status || []) as FieldOpt[]);
        setTicketStatusOptions(((ticketOptsGrouped as any).status || []) as FieldOpt[]);
      } catch {
        setCaseStatusCounts({});
        setTicketStatusCounts({});
        setCaseStatusOptions([]);
        setTicketStatusOptions([]);
      }

      try {
        const [taskCountResp, taskMeta] = await Promise.all([
          apiService.getTaskStatusCounts(),
          apiService.getTaskMeta(),
        ]);
        setTaskStatusCounts(taskCountResp.counts || {});
        // Open statuses only (matches the counts), as options for labels and ordering
        setTaskStatusOptions(
          (taskMeta.statuses || [])
            .filter((s) => !s.is_closed)
            .map((s) => ({ value: s.code, label: s.name, sort_order: s.sort_order ?? 0 }))
        );
      } catch {
        setTaskStatusCounts({});
        setTaskStatusOptions([]);
      }

      try {
        const orders = await apiService.adminListOrders({ per_page: 3 });
        setRecentOrders(Array.isArray(orders) ? orders.slice(0, 3) : []);
      } catch {
        setRecentOrders([]);
      }

      try {
        const supa = await apiService.getSupaSessionStatusSummary();
        setSupaStatus(supa);
      } catch {
        setSupaStatus(null);
      }

      try {
        setNbnStatus(await apiService.getNbnSessionStatusSummary());
      } catch {
        setNbnStatus(null);
      }

      try {
        const stats = await apiService.getOrderMonthlyStats();
        setOrderMonthlyStats(stats);
      } catch {
        setOrderMonthlyStats([]);
      }

      try {
        const reviewStats = await apiService.adminGetGoogleReviewStats();
        setGoogleReviewStats(reviewStats);
      } catch {
        setGoogleReviewStats(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(() => fetchAll(false), 120000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const caseRows = useMemo(
    () => buildStatusRows(caseStatusOptions, caseStatusCounts),
    [caseStatusOptions, caseStatusCounts]
  );
  const ticketRows = useMemo(
    () => buildStatusRows(ticketStatusOptions, ticketStatusCounts),
    [ticketStatusOptions, ticketStatusCounts]
  );

  const totalOpenCases = useMemo(
    () => Object.values(caseStatusCounts).reduce((a, b) => a + b, 0),
    [caseStatusCounts]
  );
  const taskRows = useMemo(
    () => buildStatusRows(taskStatusOptions, taskStatusCounts),
    [taskStatusOptions, taskStatusCounts]
  );
  const totalOpenTasks = useMemo(
    () => Object.values(taskStatusCounts).reduce((a, b) => a + b, 0),
    [taskStatusCounts]
  );
  const totalOpenTickets = useMemo(
    () => Object.values(ticketStatusCounts).reduce((a, b) => a + b, 0),
    [ticketStatusCounts]
  );

  const typeLabels: Record<string, string> = useMemo(() => {
    const base: Record<string, string> = {
      nbn: 'NBN',
      mobile: 'Mobile',
      voice: 'Voice',
      sonweb: 'SonWeb',
      data: 'Data',
      supa_networks: 'Supa Networks',
      domain_name: 'Domain Name',
    };
    for (const t of serviceTypes) {
      const code = (t.code || '').trim().toLowerCase();
      const name = (t.name || '').trim();
      if (code && name) base[code] = name;
    }
    return base;
  }, [serviceTypes]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const totalActive = activeNbnOutages + activeVoiceOutages + activeWebServicesOutages + activeMobileOutages;
  const totalPlanned = plannedNbnOutages + plannedVoiceOutages + plannedWebServicesOutages + plannedMobileOutages;

  const formatRetryTime = (retryTime: string | null) => {
    if (!retryTime) return 'Unknown';
    try {
      const diffMins = Math.floor((new Date(retryTime).getTime() - Date.now()) / 60000);
      if (diffMins <= 0) return 'Retrying now...';
      if (diffMins === 1) return 'in 1 min';
      return `in ${diffMins} min`;
    } catch { return 'Unknown'; }
  };

  const outageRows = [
    { type: 'Internet', active: activeNbnOutages, planned: plannedNbnOutages },
    { type: 'Mobile', active: activeMobileOutages, planned: plannedMobileOutages },
    { type: 'Voice', active: activeVoiceOutages, planned: plannedVoiceOutages },
    { type: 'Web Services', active: activeWebServicesOutages, planned: plannedWebServicesOutages },
  ];

  const countLinkSx = {
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color: 'primary.main',
    textDecoration: 'none',
    minWidth: 28,
    textAlign: 'right' as const,
    '&:hover': { textDecoration: 'underline' },
  };


  return (
    <Box sx={{ width: '100%' }}>
      {connectionFailures.length > 0 && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          action={
            <Tooltip title={carrierBannerExpanded ? 'Minimise' : 'Expand'}>
              <IconButton
                size="small"
                color="warning"
                onClick={() => setCarrierBannerExpanded(v => !v)}
                aria-label={carrierBannerExpanded ? 'Minimise carrier alert' : 'Expand carrier alert'}
              >
                {carrierBannerExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <AlertTitle sx={{ mb: carrierBannerExpanded ? 1 : 0 }}>
              Carrier connection issues
              {!carrierBannerExpanded && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                  — {connectionFailures.length} carrier{connectionFailures.length > 1 ? 's' : ''} affected
                </Typography>
              )}
            </AlertTitle>
          </Box>
          <Collapse in={carrierBannerExpanded}>
            {connectionFailures.map((failure, idx) => (
              <Box key={failure.carrier_id} sx={{ mt: idx > 0 ? 1.5 : 0 }}>
                <Typography variant="body2" fontWeight={600}>{failure.name}</Typography>
                <Typography variant="body2" color="text.secondary">{failure.error_message || 'Connection failed'}</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                  <Chip label={`#${failure.failure_count}`} size="small" color="warning" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">Retry {formatRetryTime(failure.next_retry_time)}</Typography>
                </Box>
              </Box>
            ))}
          </Collapse>
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overview of accounts, CRM workload, and outage status
          </Typography>
        </Box>
        <Tooltip title="Refresh now">
          <span>
            <IconButton
              color="primary"
              aria-label="Refresh dashboard"
              onClick={() => fetchAll(false)}
              sx={{ mt: 0.5, bgcolor: 'action.hover' }}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard title="Accounts" value={totalAccounts} icon={<PeopleIcon sx={{ fontSize: 28 }} />} color="#0d1f3c" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard title="Active Services" value={totalServices} icon={<ServicesIcon sx={{ fontSize: 28 }} />} color="#3b9eff" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard title="Active outages" value={totalActive} icon={<OutageIcon sx={{ fontSize: 28 }} />} color="#dc2626" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard title="Planned outages" value={totalPlanned} icon={<PlannedIcon sx={{ fontSize: 28 }} />} color="#7fa8d4" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ height: '100%', minHeight: 100, display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ color: '#f59e0b', opacity: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 2, bgcolor: '#f59e0b14', flexShrink: 0 }}>
                <StarIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Google Reviews
                </Typography>
                {googleReviewStats === undefined ? (
                  <CircularProgress size={18} sx={{ mt: 0.5 }} />
                ) : googleReviewStats === null ? (
                  <Typography variant="caption" color="text.secondary">Not configured</Typography>
                ) : (
                  <>
                    <Typography variant="h4" component="span" sx={{ color: '#f59e0b', fontWeight: 700, letterSpacing: '-0.02em' }}>
                      {googleReviewStats.average_rating != null ? googleReviewStats.average_rating.toFixed(1) : '—'}
                    </Typography>
                    {googleReviewStats.total_review_count != null && (
                      <Typography
                        component="a"
                        href={googleReviewStats.place_id ? `https://www.google.com/maps/place/?q=place_id:${googleReviewStats.place_id}` : '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{ display: 'block', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {googleReviewStats.total_review_count} reviews
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Supa Networks + Orders & Revenue row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {supaStatus !== null && supaStatus.total > 0 && (
          <Grid item xs={12} md={nbnStatus !== null && nbnStatus.total > 0 ? 3 : 4}>
            <NetworkStatusTile title="Supa Networks" status={supaStatus} reportType="supa_networks" reportLabel="View Supa services report" />
          </Grid>
        )}
        {nbnStatus !== null && nbnStatus.total > 0 && (
          <Grid item xs={12} md={3}>
            <NetworkStatusTile
              title="NBN Co"
              status={nbnStatus}
              reportType="nbn"
              reportLabel="View NBN services report"
              pendingNote={nbnStatus.live_status ? undefined : 'Pending API access: live status is not yet available, so all active services are shown as online.'}
            />
          </Grid>
        )}
        <Grid item xs={12} md={supaStatus !== null && supaStatus.total > 0 && nbnStatus !== null && nbnStatus.total > 0 ? 6 : (supaStatus !== null && supaStatus.total > 0) || (nbnStatus !== null && nbnStatus.total > 0) ? 8 : 12}>
          <Card sx={{ ...crmCardSx, height: '100%' }}>
            <CardContent sx={{ py: 2.5, height: '100%', display: 'flex', flexDirection: 'column', '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Box sx={{ color: '#059669', display: 'flex', p: 1, borderRadius: 2, bgcolor: 'rgba(5,150,105,0.08)' }}>
                  <MrrIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Orders and MRR
                  </Typography>
                </Box>
                {orderMonthlyStats.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    FY {orderMonthlyStats[0].month.slice(0, 4)}/{orderMonthlyStats[11]?.month.slice(2, 4)}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, minHeight: 160, borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                {orderMonthlyStats.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="text.disabled">No data</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={160}>
                    <ComposedChart data={orderMonthlyStats.map(d => ({
                      ...d,
                      label: new Date(d.month + '-01').toLocaleString('en-AU', { month: 'short' }),
                    }))} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        yAxisId="orders"
                        orientation="left"
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                        width={24}
                      />
                      <YAxis
                        yAxisId="mrr"
                        orientation="right"
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v: number) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                        width={40}
                      />
                      <RechartsTooltip
                        contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e5e7eb' }}
                        formatter={(value: number, name: string) =>
                          name === 'MRR' ? [`$${value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`, 'MRR'] : [value, 'Orders']
                        }
                      />
                      <Legend
                        iconSize={8}
                        wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                        formatter={(v) => <span style={{ color: '#6b7280' }}>{v}</span>}
                      />
                      <Bar yAxisId="orders" dataKey="order_count" name="Orders" fill="#3b9eff" radius={[3, 3, 0, 0]} maxBarSize={28} opacity={0.85} />
                      <Line yAxisId="mrr" dataKey="total_mrr" name="MRR" stroke="#059669" strokeWidth={2} dot={{ r: 3, fill: '#059669' }} activeDot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cases / Support Desk / Tasks / Recent Orders row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={crmCardSx}>
            <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Box sx={{ color: '#0d1f3c', display: 'flex', p: 1, borderRadius: 2, bgcolor: 'rgba(13,31,60,0.06)' }}>
                  <CaseIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Cases
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
                  {totalOpenCases.toLocaleString()}
                </Typography>
              </Box>
              {caseRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No open cases</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {caseRows.map(({ value, label, count }) => (
                    <Box
                      key={value}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        py: 1.25,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ pr: 2 }}>
                        {label}
                      </Typography>
                      <Typography
                        component={RouterLink}
                        to={`/crm/cases?status=${encodeURIComponent(value)}`}
                        variant="body2"
                        sx={countLinkSx}
                      >
                        {count.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                <Typography component={RouterLink} to="/crm/cases" variant="caption" color="primary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  View all cases
                </Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={crmCardSx}>
            <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Box sx={{ color: '#0369a1', display: 'flex', p: 1, borderRadius: 2, bgcolor: 'rgba(3,105,161,0.08)' }}>
                  <TicketIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Support desk
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
                  {totalOpenTickets.toLocaleString()}
                </Typography>
              </Box>
              {ticketRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No open tickets</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {ticketRows.map(({ value, label, count }) => (
                    <Box
                      key={value}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        py: 1.25,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ pr: 2 }}>
                        {label}
                      </Typography>
                      <Typography
                        component={RouterLink}
                        to={`/crm/support-desk?status=${encodeURIComponent(value)}`}
                        variant="body2"
                        sx={countLinkSx}
                      >
                        {count.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                <Typography component={RouterLink} to="/crm/support-desk" variant="caption" color="primary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  View all tickets
                </Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={crmCardSx}>
            <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Box sx={{ color: '#0f766e', display: 'flex', p: 1, borderRadius: 2, bgcolor: 'rgba(15,118,110,0.08)' }}>
                  <TaskIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Tasks
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
                  {totalOpenTasks.toLocaleString()}
                </Typography>
              </Box>
              {taskRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No open tasks</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {taskRows.map(({ value, label, count }) => (
                    <Box
                      key={value}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        py: 1.25,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ pr: 2 }}>
                        {label}
                      </Typography>
                      <Typography
                        component={RouterLink}
                        to={`/crm/tasks?status=${encodeURIComponent(value)}`}
                        variant="body2"
                        sx={countLinkSx}
                      >
                        {count.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                <Typography component={RouterLink} to="/crm/tasks" variant="caption" color="primary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  View all tasks
                </Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={crmCardSx}>
            <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Box sx={{ color: '#7c3aed', display: 'flex', p: 1, borderRadius: 2, bgcolor: 'rgba(124,58,237,0.08)' }}>
                  <OrdersIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Recent Orders
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
                  {recentOrders.length}
                </Typography>
              </Box>
              {recentOrders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No recent orders</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {recentOrders.map((order) => (
                    <Box
                      key={order.id}
                      component={RouterLink}
                      to={`/admin/orders/${order.id}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.25,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': { bgcolor: 'action.hover', mx: -1, px: 1, borderRadius: 1 },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {order.order_reference}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {order.customer_name || `${order.first_name || ''} ${order.last_name || ''}`.trim() || order.email}
                        </Typography>
                      </Box>
                      <Chip
                        label={order.status?.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, flexShrink: 0, textTransform: 'capitalize', fontSize: '0.7rem' }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                <Typography component={RouterLink} to="/admin/orders" variant="caption" color="primary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  View all orders
                </Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="caption" sx={{ mb: 1.5, display: 'block', color: '#8a93a0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Active Services By Type
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(activeServicesByType).map(([type, count]) => (
                  <Chip
                    key={type}
                    label={`${typeLabels[type] || type}: ${count}`}
                    size="medium"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
                {Object.keys(activeServicesByType).length === 0 && (
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="caption" sx={{ mb: 1.5, display: 'block', color: '#8a93a0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Outages by type
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Active</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Planned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outageRows.map((row) => (
                    <TableRow key={row.type}>
                      <TableCell>{row.type}</TableCell>
                      <TableCell align="right">
                        <Chip label={row.active} size="small" color={row.active > 0 ? 'error' : 'default'} variant={row.active > 0 ? 'filled' : 'outlined'} sx={{ minWidth: 36 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={row.planned} size="small" color="info" variant="outlined" sx={{ minWidth: 36 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
